import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Layout, { EmptyState, Pager, Spinner, StatTile } from '../components/Layout';
import Modal from '../components/Modal';
import StatusBadge, { EditTypeChip, UrgentPill } from '../components/StatusBadge';
import CaseNotes from '../components/CaseNotes';
import SecurityTab from './SecurityTab';
import FeeRecalculation from './FeeRecalculation';
import FeeReport from '../components/FeeReport';
import WatchFolders from './WatchFolders';
import { useSocket } from '../context/SocketContext';
import { useCases } from '../hooks/useCases';
import {
  cases as casesApi,
  commissions as commissionsApi,
  errorMessage,
  feePolicy as feePolicyApi,
  stats as statsApi,
  users as usersApi,
} from '../utils/api';
import { formatDateTime, formatDuration, formatNumber, formatToman, isolate } from '../utils/format';
import { SELECTABLE_EDIT_TYPES } from '../utils/constants';

const TABS = [
  { id: 'overview', label: 'نمای کلی' },
  { id: 'cases', label: 'پرونده‌ها' },
  { id: 'users', label: 'کاربران' },
  { id: 'fees', label: 'گزارش کارانه' },
  { id: 'commissions', label: 'ماتریس کارانه' },
  { id: 'policy', label: 'سیاست کارانه' },
  { id: 'studies', label: 'تصاویر' },
  { id: 'security', label: 'امنیت' },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState('overview');

  return (
    <Layout
      title="مدیریت سامانه"
      subtitle="آمار، کاربران، کارانه و پیگیری پرونده‌ها"
      tabs={TABS}
      activeTab={tab}
      onTabChange={setTab}
    >
      {tab === 'overview' && <Overview />}
      {tab === 'cases' && <CasesTab />}
      {tab === 'users' && <UsersTab />}
      {tab === 'fees' && <FeeReport isAdmin />}
      {tab === 'commissions' && <CommissionsTab />}
      {tab === 'policy' && (
        <>
          <PolicyTab />
          <FeeRecalculation />
        </>
      )}
      {tab === 'studies' && <WatchFolders />}
      {tab === 'security' && <SecurityTab />}
    </Layout>
  );
}

// ---------------------------------------------------------------- نمای کلی

function Overview() {
  const { caseVersion } = useSocket();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    statsApi
      .get()
      .then(({ data: payload }) => setData(payload))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [caseVersion]);

  if (loading) return <Spinner />;
  if (!data) return <p className="alert alert--danger">آمار بارگذاری نشد.</p>;

  return (
    <>
      <div className="tiles">
        <StatTile label="کل پرونده‌ها" value={formatNumber(data.total)} />
        <StatTile label="پرونده‌های باز" value={formatNumber(data.open_cases)} tone="brand" />
        <StatTile
          label={`کارانه‌ی ${data.fee_period?.label ?? 'ماه جاری'}`}
          value={formatToman(data.total_fee)}
          hint={`${formatNumber(data.approved_count ?? 0)} پرونده‌ی تأییدشده`}
          tone="success"
        />
        <StatTile
          label="میانگین پاسخگویی"
          value={formatDuration(data.avg_response_time)}
          tone="info"
        />
        <StatTile label="کاربران فعال" value={formatNumber(data.active_users)} />
      </div>

      <section className="panel">
        <h2 className="panel__title">پرونده‌ها بر اساس وضعیت</h2>
        <div className="statusbars">
          {Object.entries(data.by_status).map(([key, value]) => (
            <div key={key} className="statusbar">
              <StatusBadge status={key} />
              <div className="statusbar__track">
                <span
                  className={`statusbar__fill statusbar__fill--${key}`}
                  style={{ width: data.total ? `${(value / data.total) * 100}%` : 0 }}
                />
              </div>
              <span className="num statusbar__value">{formatNumber(value)}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="panel">
        <h2 className="panel__title">
          کارنامه ادیتورها — <bdi>{data.fee_period?.label}</bdi>
        </h2>
        {data.per_editor?.length ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>ادیتور</th>
                  <th>تعداد گزارش</th>
                  <th>میانگین پاسخگویی</th>
                  <th>مجموع کارانه</th>
                </tr>
              </thead>
              <tbody>
                {data.per_editor.map((row) => (
                  <tr key={row.editor_id}>
                    <td><bdi>{row.editor_name}</bdi></td>
                    <td className="num">{formatNumber(row.count)}</td>
                    <td>{formatDuration(row.avg_response_time)}</td>
                    <td className="num">{formatToman(row.fee)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="📊" title="در این ماه هنوز گزارشی تأیید نشده است." />
        )}
      </section>

    </>
  );
}

// ---------------------------------------------------------------- پرونده‌ها

function CasesTab() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [archived, setArchived] = useState(false);
  const [timeline, setTimeline] = useState(null);
  const [editing, setEditing] = useState(null);
  const [notesForId, setNotesForId] = useState(null);

  const params = useMemo(
    () => ({
      ...(search ? { q: search } : {}),
      ...(status ? { status } : {}),
      ...(archived ? { archived: 'true' } : {}),
    }),
    [search, status, archived]
  );
  const { cases, count, page, setPage, pageCount, pageSize, loading, error, reload } =
    useCases(params);

  const act = async (item, fn, message) => {
    try {
      await fn();
      toast.success(message);
      reload();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <>
      <div className="toolbar">
        <input
          type="search"
          className="input input--search"
          placeholder="جست‌وجو…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <select className="input" value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="">همه وضعیت‌ها</option>
          <option value="reception_completed">پذیرش انجام شد</option>
          <option value="ready_for_reporting">آماده گزارش‌نویسی</option>
          <option value="in_progress">در دست ادیتور</option>
          <option value="technical_review">در انتظار تأیید فنی</option>
          <option value="revision_requested">نیازمند اصلاح</option>
          <option value="report_completed">تأیید شد — آماده تحویل</option>
          <option value="closed">تحویل شد</option>
        </select>
        <label className="check check--inline">
          <input
            type="checkbox"
            checked={archived}
            onChange={(event) => setArchived(event.target.checked)}
          />
          <span>بایگانی‌شده‌ها</span>
        </label>
      </div>

      {loading && <Spinner />}
      {error && <p className="alert alert--danger">{error}</p>}

      {!loading && cases.length === 0 && <EmptyState title="پرونده‌ای یافت نشد." />}

      {cases.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>بیمار</th>
                <th>نوع</th>
                <th>وضعیت</th>
                <th>ادیتور</th>
                <th>پاسخگویی</th>
                <th>کارانه</th>
                <th>نسخه</th>
                <th>پذیرش</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {cases.map((item) => (
                <tr key={item.id} className={item.is_urgent ? 'row--urgent' : ''}>
                  <td>
                    <strong><bdi>{item.name}</bdi></strong>
                    {item.is_urgent && <> <UrgentPill /></>}
                    <br />
                    <small className="muted">
                      <bdi>{item.doctor}</bdi> · <span className="num">{item.order_number}</span>
                    </small>
                  </td>
                  <td>
                    <EditTypeChip code={item.edit_type_code} name={item.edit_type_name} />
                  </td>
                  <td>
                    <StatusBadge status={item.status} label={item.status_display} />
                  </td>
                  <td><bdi>{item.editor_name}</bdi></td>
                  <td>{formatDuration(item.response_time)}</td>
                  <td className="num">{item.fee ? formatToman(item.fee) : '—'}</td>
                  <td className="num">
                    {item.prescriptions?.length ? `${item.prescriptions.length} برگ` : '—'}
                  </td>
                  <td>{formatDateTime(item.reception_time)}</td>
                  <td className="cell--actions">
                    <button type="button" className="link" onClick={() => setNotesForId(item.id)}>
                      یادداشت‌ها
                      {item.notes?.length ? ` (${formatNumber(item.notes.length)})` : ''}
                    </button>
                    <button type="button" className="link" onClick={() => setTimeline(item)}>
                      رخدادها
                    </button>
                    <button type="button" className="link" onClick={() => setEditing(item)}>
                      ویرایش
                    </button>
                    {item.is_deleted ? (
                      <button
                        type="button"
                        className="link"
                        onClick={() => act(item, () => casesApi.restore(item.id), 'پرونده بازگردانده شد.')}
                      >
                        بازگردانی
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="link link--danger"
                        onClick={() => {
                          const reason = window.prompt(
                            `بایگانی پرونده «${isolate(item.name)}»؟
دلیل (اختیاری):`
                          );
                          if (reason === null) return;
                          act(item, () => casesApi.archive(item.id, reason), 'پرونده بایگانی شد.');
                        }}
                      >
                        بایگانی
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {cases.length > 0 && pageCount === 1 && (
        <p className="list-footnote">{formatNumber(count)} پرونده</p>
      )}

      <Pager
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        count={count}
        onChange={setPage}
      />

      <NotesModal
        item={cases.find((row) => row.id === notesForId) || null}
        onClose={() => setNotesForId(null)}
        onChanged={reload}
      />
      <TimelineModal item={timeline} onClose={() => setTimeline(null)} />
      <EditCaseModal
        item={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          reload();
        }}
      />
    </>
  );
}

function EditCaseModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setErrors({});
    setForm(
      item
        ? {
            name: item.name,
            doctor: item.doctor,
            order_number: item.order_number,
            edit_type: item.edit_type,
            is_urgent: item.is_urgent,
          }
        : null
    );
  }, [item]);

  if (!item || !form) return null;

  const update = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await casesApi.edit(item.id, { ...form, edit_type: Number(form.edit_type) });
      toast.success('اطلاعات پرونده اصلاح شد.');
      onSaved();
    } catch (err) {
      const data = err?.response?.data;
      if (data && typeof data === 'object') setErrors(data);
      toast.error(errorMessage(err, 'ویرایش انجام نشد.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title={`ویرایش پرونده «${isolate(item.name)}»`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            انصراف
          </button>
          <button type="submit" form="edit-case" className="btn btn--primary" disabled={busy}>
            {busy ? 'در حال ذخیره…' : 'ذخیره'}
          </button>
        </>
      }
    >
      <form id="edit-case" className="form" onSubmit={submit}>
        <div className="field">
          <label htmlFor="e-name">نام بیمار</label>
          <input id="e-name" value={form.name} onChange={update('name')} required />
          {errors.name && <span className="field__error">{errors.name[0]}</span>}
        </div>
        <div className="field">
          <label htmlFor="e-doctor">پزشک ارجاع‌دهنده</label>
          <input id="e-doctor" value={form.doctor} onChange={update('doctor')} required />
        </div>
        <div className="field">
          <label htmlFor="e-order">شماره نسخه</label>
          <input id="e-order" className="num" value={form.order_number} onChange={update('order_number')} required />
        </div>
        <div className="field">
          <label htmlFor="e-type">نوع ادیت</label>
          <select id="e-type" value={form.edit_type} onChange={update('edit_type')}>
            {SELECTABLE_EDIT_TYPES.map((type) => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
            {/* پرونده‌ی قدیمی ممکن است نوعی داشته باشد که دیگر انتخابی نیست */}
            {!SELECTABLE_EDIT_TYPES.some((t) => t.id === item.edit_type) && (
              <option value={item.edit_type}><bdi>{item.edit_type_name}</bdi></option>
            )}
          </select>
        </div>

        <label className={`check ${form.is_urgent ? 'is-checked' : ''}`}>
          <input
            type="checkbox"
            checked={form.is_urgent}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, is_urgent: event.target.checked }))
            }
          />
          <span>⚡ فوری — خارج از نوبت رسیدگی شود</span>
        </label>

        <p className="form__note">
          فقط اطلاعات ورودی پذیرش قابل اصلاح است. زمان‌ها، وضعیت، کارانه و
          مالکیت پرونده از اینجا تغییر نمی‌کنند و هر ویرایش در دفتر رخدادها ثبت می‌شود.
        </p>
      </form>
    </Modal>
  );
}

function NotesModal({ item, onClose, onChanged }) {
  if (!item) return null;

  return (
    <Modal open title={`یادداشت‌های «${isolate(item.name)}»`} onClose={onClose} width={560}>
      <CaseNotes
        caseId={item.id}
        notes={item.notes}
        onChanged={onChanged}
        canDelete
        disabled={item.is_deleted}
      />
      <p className="form__note">
        یادداشت‌ها ویرایش نمی‌شوند. حذف فقط در اختیار شماست و در دفتر رخدادها
        همراه متنِ حذف‌شده ثبت می‌ماند.
      </p>
    </Modal>
  );
}

function TimelineModal({ item, onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!item) return;
    setLoading(true);
    casesApi
      .timeline(item.id)
      .then(({ data }) => setEntries(data))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [item]);

  if (!item) return null;

  return (
    <Modal open title={`دفتر رخدادهای «${isolate(item.name)}»`} onClose={onClose} width={560}>
      {loading ? (
        <Spinner />
      ) : (
        <ol className="timeline">
          {entries.map((entry) => (
            <li key={entry.id}>
              <span className="timeline__dot" aria-hidden="true" />
              <div>
                <strong>{entry.action_display}</strong>
                <p className="muted">
                  <bdi>{entry.actor_name}</bdi> · <time>{formatDateTime(entry.created_at)}</time>
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}
      <p className="form__note">این رخدادها فقط‌افزودنی‌اند و قابل ویرایش یا حذف نیستند.</p>
    </Modal>
  );
}

// ---------------------------------------------------------------- کاربران

const ROLES = [
  { id: 'reception', name: 'پذیرش' },
  { id: 'imaging', name: 'تصویربرداری' },
  { id: 'editor', name: 'ادیتور' },
  { id: 'technical', name: 'مسئول فنی' },
  { id: 'admin', name: 'مدیر سامانه' },
];

function UsersTab() {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await usersApi.list({ ...(search ? { q: search } : {}), page_size: 200 });
      setRows(data.results ?? []);
      setCount(data.count ?? 0);
    } catch (error) {
      toast.error(errorMessage(error, 'فهرست کاربران بارگذاری نشد.'));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (user) => {
    try {
      await usersApi.toggleActive(user.id);
      load();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  return (
    <>
      <div className="toolbar">
        <input
          type="search"
          className="input input--search"
          placeholder="جست‌وجوی کاربر…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
          ＋ کاربر جدید
        </button>
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>نام کاربری</th>
                <th>نام</th>
                <th>نقش</th>
                <th>وضعیت</th>
                <th>تاریخ ایجاد</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((user) => (
                <tr key={user.id} className={user.is_active ? '' : 'row--muted'}>
                  <td className="num">{user.username}</td>
                  <td>{user.full_name || '—'}</td>
                  <td>{user.role_display}</td>
                  <td>
                    <span className={`badge badge--${user.is_active ? 'success' : 'muted'}`}>
                      {user.is_active ? 'فعال' : 'غیرفعال'}
                    </span>
                  </td>
                  <td>{formatDateTime(user.date_joined)}</td>
                  <td className="cell--actions">
                    <button type="button" className="link" onClick={() => setEditing(user)}>
                      ویرایش
                    </button>
                    <button type="button" className="link" onClick={() => toggle(user)}>
                      {user.is_active ? 'غیرفعال' : 'فعال'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="list-footnote">
        {formatNumber(count)} کاربر — سقفی برای تعداد کاربران وجود ندارد.
      </p>

      <UserModal
        open={creating || Boolean(editing)}
        user={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={() => {
          setCreating(false);
          setEditing(null);
          load();
        }}
      />
    </>
  );
}

function UserModal({ open, user, onClose, onSaved }) {
  const blank = { username: '', full_name: '', phone: '', email: '', role: 'reception', password: '' };
  const [form, setForm] = useState(blank);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setErrors({});
    setForm(
      user
        ? {
            username: user.username,
            full_name: user.full_name || '',
            phone: user.phone || '',
            email: user.email || '',
            role: user.role,
            password: '',
          }
        : blank
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, open]);

  const update = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    const payload = { ...form };
    if (user && !payload.password) delete payload.password;

    try {
      if (user) await usersApi.update(user.id, payload);
      else await usersApi.create(payload);
      toast.success(user ? 'کاربر به‌روزرسانی شد.' : 'کاربر ساخته شد.');
      onSaved();
    } catch (error) {
      const data = error?.response?.data;
      if (data && typeof data === 'object') setErrors(data);
      toast.error(errorMessage(error, 'ذخیره کاربر انجام نشد.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      title={user ? `ویرایش «${user.username}»` : 'کاربر جدید'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            انصراف
          </button>
          <button type="submit" form="user-form" className="btn btn--primary" disabled={busy}>
            {busy ? 'در حال ذخیره…' : 'ذخیره'}
          </button>
        </>
      }
    >
      <form id="user-form" className="form" onSubmit={submit}>
        <div className="field">
          <label htmlFor="u-username">نام کاربری</label>
          <input id="u-username" dir="ltr" value={form.username} onChange={update('username')} required />
          {errors.username && <span className="field__error">{errors.username[0]}</span>}
        </div>

        <div className="field">
          <label htmlFor="u-fullname">نام و نام خانوادگی</label>
          <input id="u-fullname" value={form.full_name} onChange={update('full_name')} />
        </div>

        <div className="field">
          <label htmlFor="u-role">نقش</label>
          <select id="u-role" value={form.role} onChange={update('role')}>
            {ROLES.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
          {errors.role && <span className="field__error">{errors.role[0]}</span>}
        </div>

        <div className="field">
          <label htmlFor="u-phone">شماره تماس</label>
          <input id="u-phone" dir="ltr" value={form.phone} onChange={update('phone')} />
        </div>

        <div className="field">
          <label htmlFor="u-password">{user ? 'رمز عبور جدید (خالی = بدون تغییر)' : 'رمز عبور'}</label>
          <input
            id="u-password"
            type="password"
            dir="ltr"
            autoComplete="new-password"
            value={form.password}
            onChange={update('password')}
            required={!user}
          />
          {errors.password && <span className="field__error">{errors.password[0]}</span>}
          <span className="field__hint">دست‌کم ۸ نویسه، نه کاملاً عددی و نه رمزی رایج.</span>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------- ماتریس کارانه

function CommissionsTab() {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = useCallback(() => {
    commissionsApi
      .matrix()
      .then(({ data }) => setMatrix(data))
      .catch(() => setMatrix(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const save = async (editorId, editType, value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount < 0) {
      toast.error('مبلغ نامعتبر است.');
      return;
    }
    const key = `${editorId}-${editType}`;
    setSaving(key);
    try {
      await commissionsApi.upsert({ editor: editorId, edit_type: editType, amount });
      toast.success('کارانه ذخیره شد.');
      load();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <Spinner />;
  if (!matrix) return <p className="alert alert--danger">ماتریس بارگذاری نشد.</p>;
  if (matrix.rows.length === 0) {
    return <EmptyState icon="💰" title="ادیتور فعالی تعریف نشده است." hint="ابتدا از تب کاربران، ادیتور بسازید." />;
  }

  return (
    <section className="panel">
      <h2 className="panel__title">مبلغ پایه هر ادیتور به تفکیک نوع ادیت (تومان)</h2>
      <p className="panel__hint">
        خانه‌های خاکستری از مقدار پیش‌فرض ({formatToman(matrix.default_amount)}) استفاده می‌کنند.
        با تغییر مقدار و خروج از خانه، ذخیره می‌شود.
      </p>

      <div className="table-wrap">
        <table className="table table--matrix">
          <thead>
            <tr>
              <th>ادیتور</th>
              {matrix.edit_types.map((type) => (
                <th key={type.id}>{type.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.rows.map((row) => (
              <tr key={row.editor_id}>
                <th scope="row"><bdi>{row.editor_name}</bdi></th>
                {row.rates.map((rate) => (
                  <td key={rate.edit_type}>
                    <input
                      type="number"
                      min="0"
                      step="1000"
                      className={`cell-input num ${rate.is_default ? 'is-default' : ''}`}
                      defaultValue={rate.amount}
                      disabled={saving === `${row.editor_id}-${rate.edit_type}`}
                      onBlur={(event) => {
                        if (Number(event.target.value) !== rate.amount) {
                          save(row.editor_id, rate.edit_type, event.target.value);
                        }
                      }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- سیاست کارانه

function PolicyTab() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    feePolicyApi
      .get()
      .then(({ data }) => setForm(data))
      .catch(() => setForm(null));
  }, []);

  if (!form) return <Spinner />;

  const update = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { data } = await feePolicyApi.update({
        default_amount: Number(form.default_amount),
        threshold_seconds: Number(form.threshold_seconds),
        bonus_ratio: Number(form.bonus_ratio),
        penalty_ratio: Number(form.penalty_ratio),
      });
      setForm(data);
      toast.success('سیاست کارانه ذخیره شد.');
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const threshold = Number(form.threshold_seconds) || 1;
  const base = Number(form.default_amount) || 0;
  const maxFee = Math.round(base * (1 + Number(form.bonus_ratio)));
  const lateFee = Math.round(base * Number(form.penalty_ratio));

  return (
    <section className="panel">
      <h2 className="panel__title">فرمول محاسبه کارانه</h2>
      <p className="panel__hint">
        این مقادیر بدون تغییر کد قابل تنظیم‌اند. برای غیرفعال کردن پاداش سرعت، ضریب پاداش را ۰ و
        برای غیرفعال کردن جریمه‌ی تأخیر، ضریب تأخیر را ۱ بگذارید.
      </p>

      <form className="form form--grid" onSubmit={submit}>
        <div className="field">
          <label htmlFor="p-base">کارانه پایه پیش‌فرض (تومان)</label>
          <input
            id="p-base"
            type="number"
            min="0"
            className="num"
            value={form.default_amount}
            onChange={update('default_amount')}
          />
        </div>

        <div className="field">
          <label htmlFor="p-threshold">آستانه زمان پاسخگویی (ثانیه)</label>
          <input
            id="p-threshold"
            type="number"
            min="1"
            className="num"
            value={form.threshold_seconds}
            onChange={update('threshold_seconds')}
          />
          <span className="field__hint">معادل {formatDuration(threshold)}</span>
        </div>

        <div className="field">
          <label htmlFor="p-bonus">حداکثر ضریب پاداش سرعت</label>
          <input
            id="p-bonus"
            type="number"
            min="0"
            max="5"
            step="0.05"
            className="num"
            value={form.bonus_ratio}
            onChange={update('bonus_ratio')}
          />
        </div>

        <div className="field">
          <label htmlFor="p-penalty">ضریب کارانه در صورت تأخیر</label>
          <input
            id="p-penalty"
            type="number"
            min="0"
            max="1"
            step="0.05"
            className="num"
            value={form.penalty_ratio}
            onChange={update('penalty_ratio')}
          />
        </div>

        <div className="form__actions">
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? 'در حال ذخیره…' : 'ذخیره سیاست'}
          </button>
        </div>
      </form>

      <div className="callout">
        <strong>پیش‌نمایش با کارانه پایه پیش‌فرض:</strong>
        <ul>
          <li>گزارش آنی: {formatToman(maxFee)}</li>
          <li>دقیقاً روی آستانه: {formatToman(base)}</li>
          <li>پس از آستانه: {formatToman(lateFee)}</li>
        </ul>
      </div>
    </section>
  );
}
